import React, { useMemo } from 'react'
import {
  ArrowUpRightFromSquare,
  // @ts-expect-error TS6133 -- MIGRATION: not approved, fix when encountered
  Badge,
  Button,
  Heading,
  IdCardIcon,
  Paragraph,
  Popup,
} from '@quickmd-llc/design-system'
import moment from 'moment'
import { Link } from 'react-router-dom'
import { useGetPatientsPatientIdIdentityverification } from 'api/generated/patient/patient'
import { IDVerificationBadge } from './StatusBadge'
import { type PatientStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

export type IDVerificationModalProps = {
  isOpen: boolean
  patient: PatientStaffApiOutContract
  onClose: () => void
}
type VerificationResponse = {
  Status: string
  DateTime: string
  Url: string
  VerificationId: string
  PatientPhotoUrl: string
  IdentityPhotoUrl: string
  Overrides: string[]
  ExpireDate: string
}

const Preview: React.FC<{ url?: string | null; alt: string }> = ({ url, alt }) => {
  if (!url) {
    return (
      <div className='relative h-full w-full overflow-hidden rounded-lg border-b border-border-medium bg-coastal-blue-5' />
    )
  }
  const target = '_blank'
  return (
    <div className='relative'>
      <Link className='absolute right-sp-2 top-sp-2' to={url} target={target} rel='noreferrer'>
        <Button size='s' priority='secondary' isIcon>
          <ArrowUpRightFromSquare />
        </Button>
      </Link>
      <div className='flex h-full w-full overflow-hidden rounded-lg border-b border-border-medium'>
        <img className='w-full' src={url} alt={`${alt} preview`} />
      </div>
    </div>
  )
}

export const IDVerificationModal: React.FC<IDVerificationModalProps> = ({ isOpen, onClose, patient }) => {
  let verification = null
  // @ts-expect-error TS2769 -- MIGRATION: not approved, fix when encountered
  verification = useGetPatientsPatientIdIdentityverification(patient.PatientId, {
    query: { enabled: !verification },
  }).data
  const { DateTime, VerificationId, PatientPhotoUrl, IdentityPhotoUrl, ExpireDate } =
    (verification as unknown as VerificationResponse) || {}

  const createdOn = DateTime
  const verificationId = VerificationId ?? ''
  const expireOn = ExpireDate ?? ''

  const createdLabel = useMemo(() => {
    if (!createdOn) return '--'
    return moment(createdOn).format('MM/DD/YYYY')
  }, [createdOn])

  const expireLabel = useMemo(() => {
    if (!expireOn) return '--'
    return moment(expireOn).format('MM/DD/YYYY')
  }, [expireOn])

  if (!isOpen) return null

  return (
    <Popup
      onClose={onClose}
      isOpen
      showCloseIcon
      styles={{ wrapperClassName: 'w-[752px] [&>*]:!w-full max-h-[752px]' }}
      header={{
        content: (
          <div className='flex items-center gap-sp-1'>
            <IdCardIcon />
            <Heading level='h3'>ID Verification</Heading>
          </div>
        ),
      }}
      body={
        <div className='flex w-full max-w-3xl flex-col gap-sp-4'>
          {/* Details */}
          <div className='flex w-[176px] flex-col gap-sp-1'>
            <div className='flex items-center gap-sp-1'>
              <Paragraph size='xs' className='w-[80px] font-semibold text-text-dark'>
                Status
              </Paragraph>
              {/*
               // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered */}
              <IDVerificationBadge verification={patient.IdentityVerification} />
            </div>
            <div className='flex items-center gap-sp-1'>
              <Paragraph size='xs' className='w-[80px] font-semibold text-text-dark'>
                Created
              </Paragraph>
              <Paragraph size='xs' className='text-text-light'>
                {createdLabel}
              </Paragraph>
            </div>
            <div className='flex items-center gap-sp-1'>
              <Paragraph size='xs' className='w-[80px] font-semibold text-text-dark'>
                ID
              </Paragraph>
              <Paragraph size='xs' className='text-text-light'>
                {verificationId || '--'}
              </Paragraph>
            </div>
            <div className='flex items-center gap-sp-1'>
              <Paragraph size='xs' className='w-[80px] font-semibold text-text-dark'>
                Expiration
              </Paragraph>
              <Paragraph size='xs' className='text-text-light'>
                {expireLabel}
              </Paragraph>
            </div>
          </div>

          <div className='grid min-h-[368px] grid-cols-2 gap-sp-3'>
            <Preview url={PatientPhotoUrl ?? undefined} alt='Patient selfie' />
            <Preview url={IdentityPhotoUrl ?? undefined} alt='Identity photo' />
          </div>
        </div>
      }
    />
  )
}

export default IDVerificationModal
